<template>
  <v-container fluid class="my-5">
    <div class="d-flex justify-space-between align-center flex-wrap">
      <div>
        <h2 class="subheading grey--text">Cuotas</h2>
        <p class="grey--text text--darken-1">Administra las cuotas y cargos del fraccionamiento.</p>
      </div>
      <v-btn color="primary" @click="openNewFeeDialog()">
        <v-icon left>mdi-plus</v-icon>
        Nueva cuota
      </v-btn>
    </div>

    <v-tabs v-model="tab" background-color="transparent" color="primary" class="mb-4">
      <v-tab>Cuotas</v-tab>
      <v-tab>Pagos</v-tab>
      <v-tab>Morosidad</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab">
      <!-- TAB: CUOTAS -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="card in feeSummaryCards" :key="card.label">
            <v-card class="elevation-2 stat-card">
              <v-card-text>
                <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
                <div class="stat-value">{{ card.value }}</div>
                <div class="stat-label grey--text">{{ card.label }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="elevation-2 mt-2">
          <v-data-table
            :headers="feeHeaders"
            :items="fees"
            :footer-props="{
              itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
              itemsPerPageText: 'Filas por página'
            }"
            :no-data-text="'No hay cuotas registradas'"
            class="elevation-0"
          >
            <template v-slot:item.amount="{ item }">
              {{ formatCurrency(item.amount) }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small class="mr-2" v-on="on" @click="editFee(item)">mdi-pencil</v-icon>
                </template>
                <span>Editar</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on" @click="toggleFeeStatus(item)">mdi-toggle-switch-outline</v-icon>
                </template>
                <span>Activar / desactivar</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- TAB: PAGOS -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="card in paymentSummaryCards" :key="card.label">
            <v-card class="elevation-2 stat-card">
              <v-card-text>
                <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
                <div class="stat-value">{{ card.value }}</div>
                <div class="stat-label grey--text">{{ card.label }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="elevation-2 mt-2">
          <v-data-table
            :headers="paymentHeaders"
            :items="payments"
            :footer-props="{
              itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
              itemsPerPageText: 'Filas por página'
            }"
            :no-data-text="'No hay pagos registrados'"
            class="elevation-0"
          >
            <template v-slot:item.amount="{ item }">
              {{ formatCurrency(item.amount) }}
            </template>
            <template v-slot:item.voucher="{ item }">
              <v-btn text small color="primary" @click="viewVoucher(item)">
                <v-icon left small>mdi-file-document-outline</v-icon>
                Ver
              </v-btn>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom v-if="item.status === 'Pendiente'">
                <template v-slot:activator="{ on }">
                  <v-icon small class="mr-2" color="green" v-on="on" @click="validatePayment(item)">mdi-check-circle-outline</v-icon>
                </template>
                <span>Validar pago</span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.status === 'Pendiente'">
                <template v-slot:activator="{ on }">
                  <v-icon small color="red" v-on="on" @click="rejectPayment(item)">mdi-close-circle-outline</v-icon>
                </template>
                <span>Rechazar pago</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- TAB: MOROSIDAD -->
      <v-tab-item>
        <v-row>
          <v-col cols="12" sm="6" md="3" v-for="card in delinquencyCards" :key="card.label">
            <v-card class="elevation-2 stat-card">
              <v-card-text>
                <v-icon :color="card.color" class="mb-2" large>{{ card.icon }}</v-icon>
                <div class="stat-value">{{ card.value }}</div>
                <div class="stat-label grey--text">{{ card.label }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-card class="elevation-2 mt-2">
          <v-data-table
            :headers="delinquentHeaders"
            :items="delinquents"
            :footer-props="{
              itemsPerPageOptions: [{text: '5', value: 5}, {text: '10', value: 10}, {text: 'Todos', value: -1}],
              itemsPerPageText: 'Filas por página'
            }"
            :no-data-text="'No hay morosos registrados'"
            class="elevation-0"
          >
            <template v-slot:item.totalDebt="{ item }">
              {{ formatCurrency(item.totalDebt) }}
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip small :color="statusColor(item.status)" text-color="white">{{ item.status }}</v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small class="mr-2" v-on="on" @click="viewAccountStatement(item)">mdi-file-account-outline</v-icon>
                </template>
                <span>Ver estado de cuenta</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small class="mr-2" v-on="on" @click="registerPayment(item)">mdi-cash-plus</v-icon>
                </template>
                <span>Registrar pago</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-icon small v-on="on" @click="sendReminder(item)">mdi-bell-ring-outline</v-icon>
                </template>
                <span>Enviar recordatorio</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Diálogo Nueva Cuota -->
    <v-dialog v-model="dialogNewFee" persistent scrollable max-width="600">
      <v-form @submit.prevent="saveFee()">
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>Nueva cuota</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn class="align-self-end" color="red" icon @click="dialogNewFee = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-layout row wrap class="mt-4">
              <v-flex xs12>
                <v-text-field
                  v-model="newFee.concept"
                  label="Nombre / concepto"
                  prepend-icon="mdi-tag-outline"
                  outlined
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select
                  v-model="newFee.type"
                  :items="['Ordinaria', 'Extraordinaria', 'Otro']"
                  label="Tipo"
                  prepend-icon="mdi-shape-outline"
                  outlined
                  class="mr-2"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="newFee.amount"
                  label="Monto"
                  prepend-icon="mdi-currency-usd"
                  type="number"
                  outlined
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select
                  v-model="newFee.periodicity"
                  :items="['Mensual', 'Bimestral', 'Anual', 'Única']"
                  label="Periodicidad"
                  prepend-icon="mdi-calendar-refresh-outline"
                  outlined
                  class="mr-2"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="newFee.dueDate"
                  label="Fecha de vencimiento"
                  prepend-icon="mdi-calendar-clock-outline"
                  placeholder="Día 10 o 30/09/2026"
                  outlined
                ></v-text-field>
              </v-flex>
              <v-flex xs12 sm6>
                <v-select
                  v-model="newFee.properties"
                  :items="['Todas las propiedades', 'Sólo Casa Club', 'Selección manual']"
                  label="Propiedades a las que aplica"
                  prepend-icon="mdi-home-group"
                  outlined
                  class="mr-2"
                ></v-select>
              </v-flex>
              <v-flex xs12 sm6>
                <v-text-field
                  v-model="newFee.surcharge"
                  label="Recargo (%)"
                  prepend-icon="mdi-percent-outline"
                  type="number"
                  outlined
                ></v-text-field>
              </v-flex>
              <v-flex xs12>
                <v-switch v-model="newFee.active" label="Cuota activa"></v-switch>
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" text @click="dialogNewFee = false">Cancelar</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" type="submit">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Diálogo Comprobante -->
    <v-dialog v-model="dialogVoucher" max-width="450">
      <v-card>
        <v-card-title class="headline">Comprobante de pago</v-card-title>
        <v-card-text>
          <v-icon large color="primary" class="mb-2">mdi-file-pdf-box</v-icon>
          <div>{{ selectedVoucher }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="dialogVoucher = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  propertySummary,
  feeConcepts,
  paymentsList,
  delinquentHouses,
  statusColor
} from '@/components/office/officeMockData'

export default {
  name: 'oficina_cuotas',
  data () {
    return {
      tab: 0,
      propertySummary,
      fees: feeConcepts,
      payments: paymentsList,
      delinquents: delinquentHouses,
      dialogNewFee: false,
      dialogVoucher: false,
      selectedVoucher: '',
      newFee: this.emptyFee(),
      feeHeaders: [
        { text: 'Concepto', value: 'concept' },
        { text: 'Tipo', value: 'type' },
        { text: 'Monto', value: 'amount' },
        { text: 'Periodicidad', value: 'periodicity' },
        { text: 'Fecha de vencimiento', value: 'dueDate' },
        { text: 'Propiedades aplicadas', value: 'properties' },
        { text: 'Estado', value: 'status' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ],
      paymentHeaders: [
        { text: 'Fecha', value: 'date' },
        { text: 'Casa', value: 'house' },
        { text: 'Residente', value: 'resident' },
        { text: 'Concepto', value: 'concept' },
        { text: 'Monto', value: 'amount' },
        { text: 'Método', value: 'method' },
        { text: 'Comprobante', value: 'voucher', sortable: false },
        { text: 'Estado', value: 'status' },
        { text: 'Acción', value: 'actions', sortable: false }
      ],
      delinquentHeaders: [
        { text: 'Casa', value: 'house' },
        { text: 'Propietario', value: 'owner' },
        { text: 'Adeudo total', value: 'totalDebt' },
        { text: 'Cuotas pendientes', value: 'pendingFees' },
        { text: 'Días de atraso', value: 'daysLate' },
        { text: 'Último pago', value: 'lastPayment' },
        { text: 'Estado', value: 'status' },
        { text: 'Acciones', value: 'actions', sortable: false }
      ]
    }
  },
  computed: {
    feeSummaryCards () {
      const active = this.fees.filter(f => f.status === 'Activa').length
      return [
        { label: 'Cuotas activas', value: active, icon: 'mdi-receipt-text-outline', color: 'primary' },
        { label: 'Cobrado este mes', value: this.formatCurrency(this.propertySummary.collectedMonth), icon: 'mdi-cash-check', color: 'green' },
        { label: 'Pendiente de cobro', value: this.formatCurrency(this.propertySummary.totalPending), icon: 'mdi-cash-remove', color: 'orange' },
        { label: 'Propiedades con adeudo', value: this.propertySummary.overdue, icon: 'mdi-home-alert-outline', color: 'red' }
      ]
    },
    paymentSummaryCards () {
      const validated = this.payments.filter(p => p.status === 'Validado')
      const pending = this.payments.filter(p => p.status === 'Pendiente')
      const rejected = this.payments.filter(p => p.status === 'Rechazado')
      const totalReceived = validated.reduce((sum, p) => sum + p.amount, 0)
      return [
        { label: 'Total recibido', value: this.formatCurrency(totalReceived), icon: 'mdi-cash-multiple', color: 'green' },
        { label: 'Pagos pendientes', value: pending.length, icon: 'mdi-clock-outline', color: 'orange' },
        { label: 'Pagos validados', value: validated.length, icon: 'mdi-check-circle-outline', color: 'green' },
        { label: 'Pagos rechazados', value: rejected.length, icon: 'mdi-close-circle-outline', color: 'red' }
      ]
    },
    delinquencyCards () {
      const totalDebt = this.delinquents.reduce((sum, d) => sum + d.totalDebt, 0)
      const overNinety = this.delinquents.filter(d => d.status === '+90 días').length
      const overdue = this.delinquents.filter(d => d.daysLate > 0).length
      return [
        { label: 'Total de morosos', value: this.delinquents.length, icon: 'mdi-account-alert-outline', color: 'red' },
        { label: 'Monto total adeudado', value: this.formatCurrency(totalDebt), icon: 'mdi-cash-remove', color: 'orange' },
        { label: 'Adeudos vencidos', value: overdue, icon: 'mdi-calendar-remove-outline', color: 'orange' },
        { label: 'Adeudos +90 días', value: overNinety, icon: 'mdi-alert-octagon-outline', color: 'red darken-3' }
      ]
    }
  },
  methods: {
    statusColor,
    formatCurrency (value) {
      return '$' + Number(value).toLocaleString('es-MX')
    },
    emptyFee () {
      return {
        concept: '',
        type: 'Ordinaria',
        amount: '',
        periodicity: 'Mensual',
        dueDate: '',
        properties: 'Todas las propiedades',
        surcharge: '',
        active: true
      }
    },
    openNewFeeDialog () {
      this.newFee = this.emptyFee()
      this.dialogNewFee = true
    },
    editFee (item) {
      this.newFee = { ...item }
      this.dialogNewFee = true
    },
    saveFee () {
      this.fees.push({
        concept: this.newFee.concept,
        type: this.newFee.type,
        amount: Number(this.newFee.amount) || 0,
        periodicity: this.newFee.periodicity,
        dueDate: this.newFee.dueDate,
        properties: this.propertySummary.total,
        status: this.newFee.active ? 'Activa' : 'Inactiva'
      })
      this.dialogNewFee = false
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_change_message', 'Cuota creada (simulado)')
      this.$store.commit('snackbar_activate_true')
    },
    toggleFeeStatus (item) {
      item.status = item.status === 'Activa' ? 'Inactiva' : 'Activa'
    },
    viewVoucher (item) {
      this.selectedVoucher = item.voucher
      this.dialogVoucher = true
    },
    validatePayment (item) {
      item.status = 'Validado'
    },
    rejectPayment (item) {
      item.status = 'Rechazado'
    },
    viewAccountStatement (item) {
      this.$router.push('/account_statements')
    },
    registerPayment (item) {
      this.tab = 1
    },
    sendReminder (item) {
      this.$store.commit('snackbar_change_color', 'success')
      this.$store.commit('snackbar_change_message', `Recordatorio enviado a ${item.owner} (simulado)`)
      this.$store.commit('snackbar_activate_true')
    }
  }
}
</script>

<style scoped>
.stat-card {
  text-align: center;
  height: 100%;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.85rem;
}
</style>
